import produce from 'immer';
import { isEmpty } from 'lodash';
import agent from 'src/api/agent';
import {
  useCommunications,
  useDocumentReferences,
  usePatient,
  useServiceRequest,
} from 'src/@nicheaim/fhir-react';
import Page from 'src/components/Page';
import { useParams } from 'react-router';
import Iconify from 'src/components/Iconify';
import useSettings from 'src/hooks/useSettings';
import ReferralSummary from '../ReferralSummary';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { memo, useEffect, useState } from 'react';
import { TLink } from 'src/components/Breadcrumbs';
import { PatientSummary } from 'src/sections/crs/patient';
import { referralService } from 'src/crs/referral/services';
import NoteAddForm from 'src/sections/crs/common/NoteAddForm';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import { TableCustom } from 'src/sections/crs/common/TableCustom';
import { TABLE_HEAD_NOTES } from 'src/sections/crs/common/table-head';
import { PatientWrapper } from 'src/@nicheaim/fhir-base/wrappers/Patient';
import { ServiceRequest } from 'src/@nicheaim/fhir-base/mappings/ServiceRequest';
import { Box, Container, Grid, IconButton, Stack, Typography } from '@mui/material';
import { CommunicationWrapper } from 'src/@nicheaim/fhir-base/wrappers/Communication';
import { mapCommunicationsToNotesDisplay } from 'src/sections/crs/common/common-utils';
import { ServiceRequestWrapper } from 'src/@nicheaim/fhir-base/wrappers/ServiceRequest';
import { mapServiceRequesToReferralSummary } from 'src/sections/crs/common/common-utils';
import { DocumentReferenceWrapper } from 'src/@nicheaim/fhir-base/wrappers/DocumentReference';
import { DocumentList } from 'src/sections/crs/patient/components/patientDocuments/DocumentList';

const getIdFromReference = (reference: string): string => reference.split('/')?.[1];

const ChildReferralDetails = memo(() => {
  const { themeStretch } = useSettings();

  const { id: referralId = null } = useParams();

  const [patientId, setPatientId] = useState<string | null>(null);

  const [communicationsQuery, setCommunicationsQuery] = useState({});
  const [documentReferencesQuery, setDocumentReferencesQuery] = useState({});
  const [openNote, setOpenNote] = useState(false);
  const [parentReferral, setParentReferral] = useState('');

  const [allUsers, setAllUsers] = useState<any>([]);
  const [openCollapse, setOpenCollapse] = useState(false);
  const [referralRecord, setReferralRecord] = useState();

  const getAllUsers = async () => {
    const result = await agent.User.getAllUsers();
    setAllUsers(result);
  };

  const fetchReferralRecord = async (id: any) => {
    const result: any = await referralService.referralApiClient.getOne(id);

    setReferralRecord(result);
  };

  useEffect(() => {
    if (referralId) {
      setParentReferral(getIdFromReference(serviceRequest?.basedOn?.[0].reference || ''));
      getAllUsers();
      fetchReferralRecord(referralId);
    }
  }, [referralId]);

  const [
    serviceRequest,
    {
      isSuccess: isSuccessServiceRequest,
      refresh: refreshServiceRequest,
      update: updateServiceRequest,
    },
  ] = useServiceRequest(referralId, { map: ServiceRequestWrapper });

  const [patient] = usePatient(patientId, {
    map: PatientWrapper,
  });

  const [communications, { refresh: refreshCommunications, create: createCommunication }] =
    useCommunications({
      filter: communicationsQuery,
      map: CommunicationWrapper,
    });

  const [
    documentReferences,
    { refresh: refreshDocumentReference },
  ] = useDocumentReferences({ filter: documentReferencesQuery, map: DocumentReferenceWrapper });

  useEffect(() => {
    if (serviceRequest && isSuccessServiceRequest) {
      if (serviceRequest?.subject?.reference) {
        setPatientId(getIdFromReference(serviceRequest?.subject?.reference));
      }
      setCommunicationsQuery({ ...communicationsQuery, 'part-of': serviceRequest.id });
      setDocumentReferencesQuery({ ...documentReferencesQuery, related: serviceRequest.id });
    }
  }, [serviceRequest, isSuccessServiceRequest]);

  const handleReAssingOwned = async (ownedSelected: string) => {
    try {
      const performerServiceRequest =
        serviceRequest?.performer?.filter((p) => p.type !== 'Practitioner') || [];
      const getUser = allUsers?.find((u) => u?.id === Number(ownedSelected));

      if (!isEmpty(getUser)) {
        const practitioner = {
          reference: getUser?.fhirUri,
          display: `${getUser?.firstName} ${getUser?.lastName}`,
          type: 'Practitioner',
        };

        const performerEdit = [...performerServiceRequest, practitioner];

        const update = updateServiceRequest(
          produce(serviceRequest!, (draft) => {
            draft.performer = performerEdit;
          })
        );

        refreshServiceRequest();
      }
    } catch (err) {
      console.log('error on handleReAssingOwned');
    }
  };

  const handleCreateCommunicationFhir = async (data: any) => {
    let result: any = {};
    try {
      result = await createCommunication(data);
      handleCloseAddNote();
      refreshCommunications();
    } catch (err) {
      console.log('error on handleCreateCommunicationFhir', err);
    }

    return result;
  };

  const handleOpenNote = () => {
    setOpenNote((prev) => !prev);
  };

  const handleCloseAddNote = () => {
    setOpenNote(false);
  };

  const handleUpdateReferral = async (data: ServiceRequest) => {

    const id = data?.id;
    const isEditing = Boolean(id);

    let result: any = {};

   if(isEditing){
      result = await updateServiceRequest(
        produce(serviceRequest!, (draft) => {
          draft.supportingInfo = data.supportingInfo;
          draft.note = data.note;
        })
      );
    }

    refreshServiceRequest();
    
    return result;
  };

  const link = [
    { name: 'Home', href: PATH_DASHBOARD.root },
    { name: 'CRS' },
    { name: 'Referral List', href: PATH_DASHBOARD.crs.referral },
    parentReferral && {
      name: 'Referral Parent',
      href: `${PATH_DASHBOARD.crs.referral}/${parentReferral}`,
    },
    { name: 'Child Referral Details' },
  ].filter((item) => item) as TLink[];

  return (
    <Page title="Child Referral Details">
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs title="" heading="" links={link} />
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography variant="h4">Child Referral Details</Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'end' }}>
              <Typography sx={{ mt: 1.5 }} variant="overline">
                collapse all
              </Typography>
              <IconButton onClick={() => setOpenCollapse(!openCollapse)}>
                <Iconify
                  icon={openCollapse ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
                />
              </IconButton>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <ReferralSummary
              title="Referral Summary"
              workflowInstance={
                !isEmpty(serviceRequest) && mapServiceRequesToReferralSummary(serviceRequest)
              }
              referralRecord={referralRecord}
              users={allUsers}
              onOwnedAssign={handleReAssingOwned}
              fhirPatient={patient}
              handlers={{}}
              referralWorkflowData={{}}
              openCollapseExternal={openCollapse}
              children={
                !isEmpty(serviceRequest) ? (
                  <>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">Service</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {serviceRequest?.code?.coding?.[0]?.code}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">Referred From</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {serviceRequest?.requester?.display}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">Referred To</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {serviceRequest?.getPerfomerByType('Organization')?.display}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">Location</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {serviceRequest?.locationReference?.[0]?.display}
                      </Typography>
                    </Stack>
                  </>
                ) : null
              }
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            {patient && (
              <PatientSummary
                title="Patient Information"
                patient={patient}
                openCollapseExternal={openCollapse}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <TableCustom
              title={'Notes'}
              data={mapCommunicationsToNotesDisplay(communications, ['notes_child_referral'])}
              tableHead={TABLE_HEAD_NOTES}
              handleOpen={handleOpenNote}
              titleButton={'Add A Note'}
              openCollapseExternal={openCollapse}
            />
          </Grid>

          <Grid item xs={12}>
            <DocumentList
              patient={patient}
              documentReferences={documentReferences}
              resource={serviceRequest}
              showCollapse={true}
              refreshDocumentReference={refreshDocumentReference}
              openCollapseExternal={openCollapse}
              handleUpdateResource={handleUpdateReferral}
            />
          </Grid>
        </Grid>

        <NoteAddForm
          open={openNote}
          patient={patient}
          onCancel={handleCloseAddNote}
          resource={[serviceRequest]}
          typeNote={'notes_child_referral'}
          handleCreate={handleCreateCommunicationFhir}
        />
        
      </Container>
    </Page>
  );
});

export default ChildReferralDetails;
